/**
 * Información de la propiedad (mig 090): lectura de los campos de propiedad,
 * comerciales y legales de una UF.
 *
 * Catálogo: `property_fields` de GET /getufattributeschema (etiquetas, enums, orden).
 * Si el schema no lo trae (PROD antes de la 090) se usa un fallback mínimo.
 * Contrato: argenlotapi docs/frontend/FRONTEND_INTEGRATION_UNITS.md §2.3.
 */
import { parsePriceInputValue } from './listingCoreConstants';

/** Claves de la mig 090 que salen en detail / units / mylots / shares. */
export const UF_PROPERTY_KEYS = [
  'property_type',
  'condition',
  'delivery_date',
  'delivery_notes',
  'expenses_amount',
  'expenses_currency',
  'expenses_extraordinary_amount',
  'expenses_notes',
  'credit_eligible',
  'accepts_swap',
  'has_financing',
  'financing',
  'legal_status',
  'is_assignment',
  'agent_name',
  'agent_phone',
];

const AGENT_KEYS = new Set(['agent_name', 'agent_phone']);

/** Montos en pesos/dólares: mismo input que Precio (90.000). Cuotas y superficies no. */
export const UF_PROPERTY_MONEY_KEYS = new Set([
  'expenses_amount',
  'expenses_extraordinary_amount',
  'down_payment',
  'installment_amount',
]);

/**
 * Claves de `property_fields` que el Front no usa: las superficies quedan en Características
 * (cubierta + semicubierta) y el terreno en el tab Lote. Se filtran aunque vengan en el
 * schema y nunca se mandan en el payload.
 */
export const UF_PROPERTY_EXCLUDED_KEYS = new Set(['semi_covered_surface', 'land_surface']);

/**
 * property_type = building_type (4 tipos). Legacy `local` / `oficina` / `galpon` → `comercio`
 * (el subtipo vive en `attributes.commercial_subtype` y se ve en Características).
 */
const LEGACY_PROPERTY_TYPES = { local: 'comercio', oficina: 'comercio', galpon: 'comercio' };

export function normalizePropertyType(value) {
  if (!value) return '';
  const raw = String(value).trim().toLowerCase();
  return LEGACY_PROPERTY_TYPES[raw] || raw;
}

const CURRENCY_ENUM = [
  { value: 'ARS', label: 'Pesos (ARS)' },
  { value: 'USD', label: 'Dólares (USD)' },
];

/** Fallback mínimo con la misma forma que `property_fields`. */
export const UF_PROPERTY_FIELDS_FALLBACK = {
  groups: [
    { id: 'comercial', label: 'Comercial', order: 2, fields: [
      { key: 'expenses_amount', label: 'Expensas', type: 'number' },
      { key: 'expenses_currency', label: 'Moneda de expensas', type: 'enum', enum: CURRENCY_ENUM, default_when_amount: 'ARS' },
      { key: 'expenses_extraordinary_amount', label: 'Expensas extraordinarias', type: 'number' },
      { key: 'expenses_notes', label: 'Notas de expensas', type: 'string' },
      { key: 'credit_eligible', label: 'Apto crédito', type: 'boolean' },
      { key: 'accepts_swap', label: 'Acepta permuta', type: 'boolean' },
      { key: 'has_financing', label: 'Financiación', type: 'boolean' },
      { key: 'financing', label: 'Detalle de financiación', type: 'object', fields: [
        { key: 'down_payment', label: 'Anticipo', type: 'number' },
        { key: 'down_payment_currency', label: 'Moneda del anticipo', type: 'enum', enum: CURRENCY_ENUM },
        { key: 'installments_count', label: 'Cantidad de cuotas', type: 'number', integer: true },
        { key: 'installment_amount', label: 'Valor de la cuota', type: 'number' },
        { key: 'installment_currency', label: 'Moneda de la cuota', type: 'enum', enum: CURRENCY_ENUM },
        { key: 'notes', label: 'Notas de financiación', type: 'string' },
      ] },
    ] },
    { id: 'legal', label: 'Situación legal', order: 3, fields: [
      { key: 'legal_status', label: 'Situación legal', type: 'enum', enum: [
        { value: 'escriturada', label: 'Escriturada' },
        { value: 'escriturable', label: 'Escriturable' },
        { value: 'boleto', label: 'Boleto de compraventa' },
        { value: 'posesion', label: 'Posesión' },
        { value: 'otro', label: 'Otro' },
      ] },
      { key: 'is_assignment', label: 'Cesión de boleto', type: 'boolean' },
    ] },
    { id: 'entrega', label: 'Estado y entrega', order: 4, fields: [
      { key: 'condition', label: 'Estado', type: 'enum', enum: [
        { value: 'a_estrenar', label: 'A estrenar' },
        { value: 'excelente', label: 'Excelente' },
        { value: 'muy_bueno', label: 'Muy bueno' },
        { value: 'bueno', label: 'Bueno' },
        { value: 'a_refaccionar', label: 'A refaccionar' },
      ] },
      { key: 'delivery_date', label: 'Fecha de entrega', type: 'date' },
      { key: 'delivery_notes', label: 'Notas de entrega', type: 'string' },
    ] },
    { id: 'corredor', label: 'Corredor', order: 5, fields: [
      { key: 'agent_name', label: 'Corredor', type: 'string' },
      { key: 'agent_phone', label: 'Teléfono del corredor', type: 'string' },
    ] },
  ],
};

/** `property_fields` del schema o el fallback. */
export function resolveUfPropertyFields(schema) {
  const fromApi = schema?.property_fields;
  const catalog = fromApi && Array.isArray(fromApi.groups) && fromApi.groups.length > 0
    ? fromApi
    : UF_PROPERTY_FIELDS_FALLBACK;
  const groups = catalog.groups
    .map((group) => ({
      ...group,
      fields: (group.fields || []).filter((field) => !UF_PROPERTY_EXCLUDED_KEYS.has(field.key)),
    }))
    .filter((group) => group.fields.length > 0);
  return { ...catalog, groups };
}

/**
 * Toma las claves 090 de una fila de API (detail, unit, share).
 * Si la fila ya es un form guardado localmente (merge liviano post-save), usa su `property_info`.
 */
export function pickUfPropertyInfo(data = {}) {
  const info = {};
  UF_PROPERTY_KEYS.forEach((key) => {
    if (data?.[key] !== undefined) info[key] = data[key];
  });
  if (Object.keys(info).length === 0 && data?.property_info && typeof data.property_info === 'object') {
    return { ...data.property_info };
  }
  return info;
}

const isEmptyValue = (value) => (
  value === null || value === undefined || value === false
  || (typeof value === 'string' && value.trim() === '')
);

/** AAAA-MM-DD → DD/MM/AAAA (sin pasar por Date para no correr el día por zona horaria). */
export function formatUfDeliveryDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value || '');
}

const formatUnit = (unit) => {
  if (!unit || unit === 'ARS/USD') return undefined;
  return unit === 'm2' ? 'm²' : unit;
};

function fieldFormat(field) {
  if (field.format === 'tel' || /_phone$/.test(field.key)) return 'tel';
  if (UF_PROPERTY_MONEY_KEYS.has(field.key)) return 'money';
  return field.format;
}

function toFormField(field, idPrefix = '') {
  const type = field.type === 'date' ? 'string' : field.type;
  return {
    id: `${idPrefix}${field.key}`,
    label: field.label || field.key,
    type: type === 'number' && field.integer ? 'integer' : type,
    storage: 'column',
    unit: formatUnit(field.unit),
    options: Array.isArray(field.enum) ? field.enum : undefined,
    format: fieldFormat(field),
    _source: field,
  };
}

function displayValue(field, rawValue, info) {
  let value = rawValue;
  if (isEmptyValue(value) && field.default_when_amount && !isEmptyValue(info.expenses_amount)) {
    value = field.default_when_amount;
  }
  if (isEmptyValue(value)) return undefined;
  if (field.type === 'date') return formatUfDeliveryDate(value);
  return value;
}

/**
 * Arma schema + form para renderizar con `UfAttributesForm` (hideEmpty, disabled).
 *
 * @param {object} params
 * @param {object} params.info claves 090 (ver `pickUfPropertyInfo`)
 * @param {object} [params.schema] respuesta de getufattributeschema
 * @param {boolean} [params.hideAgent] shares: sin contacto del corredor
 * @returns {{ schema: object, form: object, hasData: boolean }}
 */
export function buildUfPropertyInfoView({
  info = {},
  schema = null,
  hideAgent = false,
} = {}) {
  const catalog = resolveUfPropertyFields(schema);
  const form = { attributes: {} };
  const groups = [];

  [...catalog.groups]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .forEach((group) => {
      const fields = [];
      (group.fields || []).forEach((field) => {
        if (hideAgent && AGENT_KEYS.has(field.key)) return;
        if (field.type === 'object') {
          const nested = info[field.key];
          if (!nested || typeof nested !== 'object') return;
          (field.fields || []).forEach((sub) => {
            const value = displayValue(sub, nested[sub.key], info);
            if (value === undefined) return;
            const formField = toFormField(sub, `${field.key}.`);
            form[formField.id] = value;
            fields.push(formField);
          });
          return;
        }
        // Con detalle de financiación el switch "Financiación" es redundante.
        if (field.key === 'has_financing' && info.financing && typeof info.financing === 'object') return;
        const value = displayValue(field, info[field.key], info);
        if (value === undefined) return;
        const formField = toFormField(field);
        form[formField.id] = value;
        fields.push(formField);
      });
      if (fields.length > 0) {
        groups.push({ id: `uf-property-${group.id}`, label: group.label, order: group.order, fields });
      }
    });

  return { schema: { groups }, form, hasData: groups.length > 0 };
}

/* ------------------------------------------------------------------ */
/* F2: wizard (paso 1 con property_type, paso 3 editable, payload dirty) */
/* ------------------------------------------------------------------ */

/**
 * Tipos del paso 1: `property_fields.property_type.enum` (4 tipos = building_type) o, si no viene,
 * los `building_types` de siempre (id = building_type).
 *
 * @param {object} apiResponse respuesta de GET /getufattributeschema (sin building_type)
 * @param {{id: string, label: string}[]} fallbackBuildingTypes
 * @returns {{ id: string, label: string, buildingType: string, commercialSubtype: string|null, isPropertyType: boolean }[]}
 */
export function resolveUfPropertyTypes(apiResponse, fallbackBuildingTypes = []) {
  const data = apiResponse?.data ?? apiResponse;
  const enumRows = data?.property_fields?.property_type?.enum;
  if (Array.isArray(enumRows) && enumRows.length > 0) {
    return enumRows
      .filter((row) => row?.value && row?.building_type && !LEGACY_PROPERTY_TYPES[row.value])
      .map((row) => ({
        id: row.value,
        label: row.label || row.value,
        buildingType: row.building_type,
        commercialSubtype: row.commercial_subtype || null,
        isPropertyType: true,
      }));
  }
  return (fallbackBuildingTypes || []).map((row) => ({
    id: row.id,
    label: row.label,
    buildingType: row.id,
    commercialSubtype: null,
    isPropertyType: false,
  }));
}

/**
 * Tipo preseleccionado en el paso 1: `property_type` derivado por la API o, si no,
 * el primer tipo cuyo building_type coincide (comercio → Comercio).
 */
export function resolveSelectedPropertyTypeId({ propertyType: rawPropertyType, buildingType } = {}, types = []) {
  const propertyType = normalizePropertyType(rawPropertyType);
  if (propertyType && types.some((t) => t.id === propertyType)) return propertyType;
  if (!buildingType) return '';
  const exact = types.find((t) => t.id === buildingType);
  if (exact) return exact.id;
  return types.find((t) => t.buildingType === buildingType)?.id || '';
}

const isBlank = (value) => value === null || value === undefined
  || (typeof value === 'string' && value.trim() === '');

function catalogFieldMap(catalog) {
  const map = {};
  (catalog?.groups || []).forEach((group) => (group.fields || []).forEach((field) => {
    map[field.key] = field;
  }));
  return map;
}

function requirementsMet(field, info) {
  const req = field?.requires;
  if (!req || typeof req !== 'object') return true;
  return Object.entries(req).every(([key, expected]) => info?.[key] === expected);
}

function toEditField(field, idPrefix = '') {
  return {
    id: `${idPrefix}${field.key}`,
    label: field.label || field.key,
    type: field.type === 'number' && field.integer ? 'integer' : field.type,
    storage: 'column',
    unit: formatUnit(field.unit),
    min: field.min,
    max: field.max,
    maxLength: field.max_length,
    options: Array.isArray(field.enum) ? field.enum : undefined,
    // Teléfonos (`format: 'tel'` o key *_phone): mismo saneo que Leads (sanitizePhoneInput).
    // Montos: `format: 'money'` → miles es-AR como el campo Precio.
    format: fieldFormat(field),
  };
}

/**
 * Schema + form plano para editar con `UfAttributesForm` en el paso 3.
 * `financing` se aplana como `financing.<sub>` y solo aparece si cumple `requires` (has_financing).
 *
 * @returns {{ schema: { groups: object[] }, form: object }}
 */
export function buildUfPropertyEditView({ info = {}, schema = null } = {}) {
  const catalog = resolveUfPropertyFields(schema);
  const form = { attributes: {} };
  const groups = [];
  [...catalog.groups]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .forEach((group) => {
      const fields = [];
      (group.fields || []).forEach((field) => {
        if (field.type === 'object') {
          const objectRequires = field.requires || (field.key === 'financing' ? { has_financing: true } : null);
          if (!requirementsMet({ requires: objectRequires }, info)) return;
          const nested = info[field.key] && typeof info[field.key] === 'object' ? info[field.key] : {};
          (field.fields || []).forEach((sub) => {
            const editField = toEditField(sub, `${field.key}.`);
            form[editField.id] = nested[sub.key] ?? '';
            fields.push(editField);
          });
          return;
        }
        if (!requirementsMet(field, info)) return;
        const editField = toEditField(field);
        const value = info[field.key];
        form[editField.id] = field.type === 'boolean' ? value === true : (value ?? '');
        fields.push(editField);
      });
      if (fields.length > 0) {
        groups.push({ id: `uf-property-edit-${group.id}`, label: group.label, order: group.order, fields });
      }
    });
  return { schema: { groups }, form };
}

/**
 * Aplica el form plano editado sobre `property_info`.
 * Apagar has_financing deja `financing` en null (la API lo borra con has_financing:false).
 */
export function applyUfPropertyFlatEdit(info = {}, flat = {}) {
  const next = { ...info };
  Object.entries(flat).forEach(([id, value]) => {
    if (id === 'attributes') return;
    if (id.includes('.')) {
      const [parent, sub] = id.split('.');
      const nested = next[parent] && typeof next[parent] === 'object' ? { ...next[parent] } : {};
      nested[sub] = value;
      next[parent] = nested;
      return;
    }
    next[id] = value;
  });
  if (next.has_financing === false) next.financing = null;
  return next;
}

function normalizeScalar(field, value) {
  if (isBlank(value)) return null;
  const type = field?.type;
  if (type === 'boolean') return value === true;
  if (type === 'number') {
    if (UF_PROPERTY_MONEY_KEYS.has(field.key) && typeof value === 'string') {
      const digits = parsePriceInputValue(value);
      if (!digits) return null;
      const n = Number(digits);
      return Number.isNaN(n) ? null : n;
    }
    const n = field.integer ? parseInt(value, 10) : parseFloat(value);
    return Number.isNaN(n) ? null : n;
  }
  if (typeof value === 'string') return value.trim();
  return value;
}

function normalizeObject(field, value) {
  if (!value || typeof value !== 'object') return null;
  const out = {};
  (field.fields || []).forEach((sub) => {
    const v = normalizeScalar(sub, value[sub.key]);
    if (v !== null) out[sub.key] = v;
  });
  return Object.keys(out).length > 0 ? out : null;
}

const sameValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Claves 090 para POST /createuserlot: SOLO las que cambiaron respecto de lo cargado.
 * Clave ausente = no se toca; null = se borra (solo si el usuario vació el campo).
 *
 * @param {object} info valores editados (`form.property_info`)
 * @param {object} loaded valores cargados (`form.property_info_loaded`)
 * @param {object} [schema] respuesta de getufattributeschema (para tipos)
 * @returns {object}
 */
export function buildUfPropertyDirtyPayload(info = {}, loaded = {}, schema = null) {
  const fields = catalogFieldMap(resolveUfPropertyFields(schema));
  const body = {};
  Object.values(fields).forEach((field) => {
    const key = field.key;
    if (key === 'property_type') return;
    if (field.type === 'object') {
      const next = normalizeObject(field, info[key]);
      const prev = normalizeObject(field, loaded[key]);
      if (!sameValue(next, prev) && info.has_financing !== false) {
        if (next) body[key] = next;
        else if (prev) body[key] = null;
      }
      return;
    }
    const next = normalizeScalar(field, info[key]);
    const prev = normalizeScalar(field, loaded[key]);
    if (field.type === 'boolean') {
      // Switch: null y false se ven igual; solo mandamos si el usuario lo tocó.
      if ((next === true) !== (prev === true)) body[key] = next === true;
      return;
    }
    if (!sameValue(next, prev)) body[key] = next;
  });

  // Moneda de expensas: con monto y sin moneda → ARS (default_when_amount).
  const currencyField = fields.expenses_currency;
  const defaultCurrency = currencyField?.default_when_amount || 'ARS';
  const amount = normalizeScalar(fields.expenses_amount || { type: 'number' }, info.expenses_amount);
  const currency = normalizeScalar(currencyField || { type: 'enum' }, info.expenses_currency);
  if (amount !== null && currency === null && ('expenses_amount' in body || 'expenses_currency' in body)) {
    body.expenses_currency = defaultCurrency;
  }
  if (body.has_financing === false) delete body.financing;
  if (body.financing && !('has_financing' in body) && loaded.has_financing !== true) {
    body.has_financing = true;
  }
  return body;
}

/** ¿El `field` de un 400 VALIDATION_ERROR es de la sección 090? Devuelve el id del form plano. */
export function resolveUfPropertyErrorFieldId(field, schema = null) {
  if (!field) return null;
  const fields = catalogFieldMap(resolveUfPropertyFields(schema));
  const raw = String(field);
  if (fields[raw]) return raw;
  if (raw.startsWith('financing.')) return raw;
  const financing = fields.financing;
  if (financing?.fields?.some((sub) => sub.key === raw)) return `financing.${raw}`;
  return null;
}

/** Toasts de `warnings` de createuserlot (`[{code, mensaje, fields}]`). */
export function readUfSaveWarnings(response) {
  const list = response?.warnings ?? response?.data?.warnings;
  if (!Array.isArray(list)) return [];
  return list
    .map((w) => (typeof w === 'string' ? w : (w?.mensaje || w?.message || w?.code)))
    .filter(Boolean);
}

